import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Input, Select } from '@components';
import type { SelectOption } from '@components/forms/Select/Select';
import type { ScenarioIssue } from './types';
import { countIssuesUnder, issuesAt } from './scenarioDraft';

export const IssueList: React.FC<{ issues: ScenarioIssue[]; pointer: string }> = ({ issues, pointer }) => {
  const matches = issuesAt(issues, pointer);
  if (!matches.length) return null;
  return (
    <ul className="mt-1 space-y-0.5" data-testid={`issues-${pointer}`}>
      {matches.map((issue, index) => (
        <li key={index} className="text-xs text-red-600 dark:text-red-400">
          {issue.message}
        </li>
      ))}
    </ul>
  );
};

export const FieldLabel: React.FC<{ label: string; hint?: string; htmlFor?: string }> = ({
  label,
  hint,
  htmlFor,
}) => (
  <label htmlFor={htmlFor} className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
    {label}
    {hint && <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">{hint}</span>}
  </label>
);

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  issues: ScenarioIssue[];
  pointer: string;
  placeholder?: string;
  hint?: string;
  multiline?: boolean;
  type?: 'text' | 'number' | 'datetime-local';
  monospace?: boolean;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  issues,
  pointer,
  placeholder,
  hint,
  multiline,
  type = 'text',
  monospace,
}) => {
  const id = `sc-${pointer.replace(/[^a-zA-Z0-9]/g, '-')}`;
  const hasError = issuesAt(issues, pointer).length > 0;
  return (
    <div>
      <FieldLabel label={label} hint={hint} htmlFor={id} />
      {multiline ? (
        <textarea
          id={id}
          className={`block w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 ${
            hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
          rows={3}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <Input
          id={id}
          type={type}
          size="sm"
          value={value}
          placeholder={placeholder}
          state={hasError ? 'error' : 'default'}
          className={monospace ? 'font-mono' : undefined}
          onChange={e => onChange(e.target.value)}
        />
      )}
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

interface NameFieldProps {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  issues: ScenarioIssue[];
  pointer: string;
}

/** Technical names are committed on blur so references can follow the rename once. */
export const NameField: React.FC<NameFieldProps> = ({ label, value, onCommit, issues, pointer }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => {
    const next = draft.trim();
    if (!next) {
      setDraft(value);
    } else if (next !== value) {
      onCommit(next);
    }
  };
  const id = `sc-${pointer.replace(/[^a-zA-Z0-9]/g, '-')}`;
  return (
    <div>
      <FieldLabel label={label} htmlFor={id} />
      <Input
        id={id}
        size="sm"
        className="font-mono"
        value={draft}
        state={issuesAt(issues, pointer).length > 0 ? 'error' : 'default'}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
        }}
      />
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

interface ListTextFieldProps {
  label: string;
  values: (string | number)[];
  onCommit: (values: string[]) => void;
  issues: ScenarioIssue[];
  pointer: string;
  hint?: string;
}

/** Comma-separated list, committed on blur so separators can be typed freely. */
export const ListTextField: React.FC<ListTextFieldProps> = ({ label, values, onCommit, issues, pointer, hint }) => {
  const joined = values.join(', ');
  const [draft, setDraft] = useState(joined);
  useEffect(() => setDraft(joined), [joined]);
  const commit = () =>
    onCommit(
      draft
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
    );
  return (
    <div>
      <FieldLabel label={label} hint={hint} />
      <Input
        size="sm"
        className="font-mono"
        value={draft}
        state={countIssuesUnder(issues, pointer) > 0 ? 'error' : 'default'}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
        }}
      />
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

interface SelectFieldProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  issues: ScenarioIssue[];
  pointer: string;
  hint?: string;
  placeholder?: string;
}

export const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  options,
  onChange,
  issues,
  pointer,
  hint,
  placeholder,
}) => {
  const known = options.some(option => option.value === value);
  const allOptions = known || !value ? options : [...options, { value, label: `${value} (?)` }];
  return (
    <div>
      <FieldLabel label={label} hint={hint} />
      <Select
        size="sm"
        value={value}
        options={allOptions}
        placeholder={placeholder}
        error={issuesAt(issues, pointer).length > 0}
        onChange={selected => onChange(Array.isArray(selected) ? selected[0] : selected)}
      />
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

interface MultiSelectFieldProps {
  label: string;
  values: string[];
  options: SelectOption[];
  onChange: (values: string[]) => void;
  issues: ScenarioIssue[];
  pointer: string;
  hint?: string;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  values,
  options,
  onChange,
  issues,
  pointer,
  hint,
}) => {
  const missing = values.filter(value => !options.some(option => option.value === value));
  return (
    <div>
      <FieldLabel label={label} hint={hint} />
      <Select
        size="sm"
        multiple
        value={values}
        options={[...options, ...missing.map(value => ({ value, label: `${value} (?)` }))]}
        onChange={selected => onChange(Array.isArray(selected) ? selected : [selected])}
      />
      <IssueList issues={issues} pointer={pointer} />
    </div>
  );
};

type Surface = 'white' | 'gray';

// The scenario tab itself is rendered on a gray panel, so top-level content starts on gray.
const SectionContext = createContext<{ level: number; surface: Surface }>({ level: 0, surface: 'gray' });

const SECTION_LEVELS: {
  container: string;
  header: string;
  title: string;
  body: string;
  surface: Surface;
}[] = [
  {
    container: 'rounded-lg bg-white shadow-sm dark:bg-gray-900',
    header: 'px-4 py-3',
    title: 'text-sm font-semibold text-gray-900 dark:text-white',
    body: 'space-y-4 px-4 py-4',
    surface: 'white',
  },
  {
    container: 'rounded-lg border-l-4 border-l-primary-400 bg-gray-50 dark:border-l-primary-500 dark:bg-gray-800/50',
    header: 'px-3 py-2',
    title: 'text-sm font-semibold text-gray-900 dark:text-white',
    body: 'space-y-3 px-3 py-3',
    surface: 'gray',
  },
  {
    container: 'rounded-md bg-white dark:bg-gray-900',
    header: 'px-3 py-1.5',
    title: 'text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300',
    body: 'space-y-3 px-3 py-3',
    surface: 'white',
  },
];

interface SectionProps {
  id?: string;
  title: string;
  subtitle?: string;
  icon?: string;
  badges?: React.ReactNode;
  issues?: ScenarioIssue[];
  pointer?: string;
  defaultOpen?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
}

export const Section: React.FC<SectionProps> = ({
  id,
  title,
  subtitle,
  icon,
  badges,
  issues,
  pointer,
  defaultOpen = true,
  actions,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const { level } = useContext(SectionContext);
  const style = SECTION_LEVELS[Math.min(level, SECTION_LEVELS.length - 1)];
  const count = issues && pointer !== undefined ? countIssuesUnder(issues, pointer) : 0;
  const borderColor =
    count > 0 ? 'border-error-300 dark:border-error-700' : 'border-gray-200 dark:border-gray-700';
  return (
    <div id={id} className={`border ${borderColor} ${style.container}`}>
      <div className={`flex items-center justify-between gap-2 ${style.header}`}>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={() => setOpen(value => !value)}
        >
          <span className="material-icons text-base text-gray-500">{open ? 'expand_less' : 'expand_more'}</span>
          {icon && <span className="material-icons text-lg text-primary-500 dark:text-primary-300">{icon}</span>}
          <span className={style.title}>{title}</span>
          {badges && <span className="flex flex-wrap items-center gap-1">{badges}</span>}
          {subtitle && <span className="truncate text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>}
          {count > 0 && (
            <Badge variant="error" size="sm">
              {count}
            </Badge>
          )}
        </button>
        {actions}
      </div>
      {open && (
        <SectionContext.Provider value={{ level: level + 1, surface: style.surface }}>
          <div className={`border-t ${borderColor} ${style.body}`}>{children}</div>
        </SectionContext.Provider>
      )}
    </div>
  );
};

export const ItemRow: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => {
  const { surface } = useContext(SectionContext);
  const background = surface === 'white' ? 'bg-gray-50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-900';
  return (
    <div className={`rounded-md border border-gray-200 p-3 dark:border-gray-700 ${background} ${className}`}>
      {children}
    </div>
  );
};

interface RowActionsProps {
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
}

export const RowActions: React.FC<RowActionsProps> = ({ index, count, onMove, onRemove }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="sm"
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
        aria-label={t('agents.scenario.moveUp')}
      >
        <span className="material-icons text-base">arrow_upward</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
        aria-label={t('agents.scenario.moveDown')}
      >
        <span className="material-icons text-base">arrow_downward</span>
      </Button>
      <Button variant="ghost" size="sm" onClick={onRemove} aria-label={t('agents.scenario.remove')}>
        <span className="material-icons text-base">delete</span>
      </Button>
    </div>
  );
};

export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button variant="outline" size="sm" onClick={onClick}>
    <span className="material-icons text-base mr-1">add</span>
    {label}
  </Button>
);

export const Toggle: React.FC<{ label: string; checked: boolean; onChange: (value: boolean) => void }> = ({
  label,
  checked,
  onChange,
}) => (
  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
    <input
      type="checkbox"
      className="h-4 w-4 rounded border-gray-300 text-primary-500"
      checked={checked}
      onChange={e => onChange(e.target.checked)}
    />
    {label}
  </label>
);
